import React, { useEffect, useMemo, useRef } from 'react'

import { Building2, ShieldCheck } from 'lucide-react'
import { useRouter } from 'next/router'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import LoadingScreen from '@/components/loading/LoadingScreen'

import {
  clearPersistedPostLoginRedirectUrl,
  createLoginUrlWithRedirect,
  getPersistedPostLoginRedirectUrl,
  getSafeRedirectPath,
} from '@/helpers/login'
import { buildPostLoginRedirectUrl } from '@/helpers/url'

import { useBillingOrganizationWithStatus } from '@/hooks/billing/useBillingOrganization'
import { useIsTrialPeriod } from '@/hooks/billing/useIsTrialPeriod'
import { usePathname } from '@/hooks/common/usePathname'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useIsOnboarding } from '@/hooks/onboarding/useIsOnboarding'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { ORG_SWITCHED_SESSION_KEY } from '@/hooks/organization/useCuiOrgSwitcher'
import { useDefaultOrganization } from '@/hooks/organization/useDefaultOrganization'
import { useOrganizations } from '@/hooks/organization/useOrganizations'

import { useOrganizationStore } from '@/stores/OrganizationStore'

/** Consumes the org-switched sessionStorage flag and shows a toast. */
const OrgSwitchToast: React.FC = () => {
  const { data: defaultOrg } = useDefaultOrganization()
  const didConsume = useRef(false)

  useEffect(() => {
    if (didConsume.current) return
    try {
      const didSwitch = sessionStorage.getItem(ORG_SWITCHED_SESSION_KEY)
      if (didSwitch && defaultOrg?.name) {
        didConsume.current = true
        sessionStorage.removeItem(ORG_SWITCHED_SESSION_KEY)
        const isCui = defaultOrg.cui_compliance_enabled === true
        const toastFn = isCui ? toast.success : toast.info
        toastFn(`Switched to ${defaultOrg.name}.`, {
          icon: isCui ? (
            <ShieldCheck className="text-green-600" size={16} />
          ) : (
            <Building2 className="text-blue-500" size={16} />
          ),
          description: isCui
            ? 'CUI-safe organization.'
            : 'Standard organization.',
        })
      }
    } catch {}
  }, [defaultOrg?.name, defaultOrg?.cui_compliance_enabled])

  return null
}

interface BillingOrgWrapper {
  children: React.ReactNode
}

const LOGIN_REDIRECT_QUERY_PARAM_KEYS = ['allow_signup'] as const

const getLoginRedirectParams = (
  searchParams: URLSearchParams,
): Record<string, string> | undefined => {
  const params = LOGIN_REDIRECT_QUERY_PARAM_KEYS.reduce<Record<string, string>>(
    (acc, key) => {
      const value = searchParams.get(key)
      if (value !== null) {
        acc[key] = value
      }
      return acc
    },
    {},
  )

  return Object.keys(params).length > 0 ? params : undefined
}

const BillingOrgWrapper: React.FC<BillingOrgWrapper> = ({ children }) => {
  const { activeOrgId } = useActiveOrgId()

  const { user, loading: authIsLoading } = useAuthInfo()

  const { isOnboarding } = useIsOnboarding()
  const { data: billingOrgStatus, isLoading: statusIsLoading } =
    useBillingOrganizationWithStatus()
  const { data: defaultOrganization } = useDefaultOrganization()

  const { data: organizations } = useOrganizations()
  const { setActiveOrgId } = useOrganizationStore()
  const { redirect: redirectToUpgrade } = useIsTrialPeriod()

  const pathname = usePathname()
  const router = useRouter()
  const { safePush } = useSafePush()

  const handledPersistedRedirectRef = useRef<string | null>(null)
  const loginRedirectRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || !user || authIsLoading) {
      return
    }

    const persistedRedirect = getPersistedPostLoginRedirectUrl()
    if (!persistedRedirect) {
      handledPersistedRedirectRef.current = null
      return
    }
    if (handledPersistedRedirectRef.current === persistedRedirect) {
      return
    }

    const safeRedirectPath = getSafeRedirectPath(persistedRedirect)
    handledPersistedRedirectRef.current = persistedRedirect
    if (safeRedirectPath === router.asPath) {
      clearPersistedPostLoginRedirectUrl()
      return
    }

    safePush(safeRedirectPath, undefined, undefined, false)
    clearPersistedPostLoginRedirectUrl()
  }, [authIsLoading, router.asPath, safePush, user?.userId])

  useEffect(() => {
    // do not set the active org id if the user is onboarding,
    // this causes a maximum recursion depth exceeded error;
    // TODO: figure out why
    if (
      !isOnboarding &&
      organizations &&
      organizations.length > 0 &&
      defaultOrganization
    ) {
      // reset active org id if none of the existing org ids equal the active org id;
      // this typically only happens when impersonating a user
      const currentOrg = organizations.find((org) => org.id === activeOrgId)
      if (!currentOrg) {
        const defaultOrg = organizations.find(
          (org) => org.id === defaultOrganization.id,
        )
        if (defaultOrg) {
          // if the default org is in the organizations array, set it as the active org
          setActiveOrgId(defaultOrg.id)
        } else {
          // if the default org is not in the organizations array (likely because the user
          // was removed from the organization), set the first org as the active org
          setActiveOrgId(organizations[0].id)
        }
      }
    }
  }, [activeOrgId, organizations?.length])

  useEffect(() => {
    if (
      // do not set the active org id if the user is onboarding,
      // this causes a maximum recursion depth exceeded error;
      // TODO: figure out why
      user &&
      !isOnboarding &&
      (activeOrgId === '' || activeOrgId === undefined) &&
      defaultOrganization &&
      organizations !== undefined &&
      organizations.length > 0
    ) {
      const org = organizations.find(
        (org) => org.id === defaultOrganization?.id,
      )
      if (org) {
        setActiveOrgId(org.id)
      } else {
        setActiveOrgId(organizations[0].id)
      }
    }
  }, [activeOrgId, organizations?.length, user?.userId])

  const isLoading = authIsLoading || statusIsLoading

  const loginUrl = useMemo(() => {
    if (typeof window === 'undefined' || user !== null) {
      return null
    }

    const currentParams = new URLSearchParams(window.location.search)
    const loginRedirectParams = getLoginRedirectParams(currentParams)
    // Keep auth-only flags as top-level login params instead of nesting them in redirect
    const cleanUrl = new URL(window.location.href)
    for (const key of LOGIN_REDIRECT_QUERY_PARAM_KEYS) {
      cleanUrl.searchParams.delete(key)
    }
    const postLoginRedirectUrl = buildPostLoginRedirectUrl(
      cleanUrl.toString(),
      pathname ?? '',
    )

    return createLoginUrlWithRedirect(postLoginRedirectUrl, loginRedirectParams)
  }, [pathname, user])

  useEffect(() => {
    if (!loginUrl) {
      loginRedirectRef.current = null
      return
    }

    if (loginRedirectRef.current === loginUrl) {
      return
    }

    loginRedirectRef.current = loginUrl
    void router.push(loginUrl)
  }, [loginUrl, router])

  if (user === null) {
    return <LoadingScreen />
  }

  if (typeof window === 'undefined' || !billingOrgStatus || isLoading) {
    return <LoadingScreen />
  }

  if (billingOrgStatus.status === 'pending') {
    return <LoadingScreen />
  }

  // null indicates that the user hasn't onboarded yet
  if (billingOrgStatus.status === 'onboarding_required' && !isOnboarding) {
    const search = typeof window !== 'undefined' ? window.location.search : ''
    safePush(`/welcome${search}`, undefined, undefined, false)
    return <LoadingScreen />
  }

  if (redirectToUpgrade) {
    // don't log an error if the user is redirected to the upgrade page
    safePush('/upgrade', undefined, undefined, false)
    return <LoadingScreen />
  }

  return (
    <>
      <OrgSwitchToast />
      {children}
    </>
  )
}

export default BillingOrgWrapper
